import { IViewProperty, NavDtFormat, NumericType } from '@common/nav';
import { INavEditContext, NavPropertyValue } from '@common/navdata';
import { InfDatePicker } from '@common/navkendo/infdatepicker';
import { constants as kconstants, DateFormat, RenderAsLinkProps } from '@common/navkendo/kutils';
import { INavCustomization } from '@common/services/settingsservice';
import { CreateCounter, fn } from '@common/utils';
import * as angular from 'angular';
import { NavLookupControl } from './lookup/lookup';
import { INavItemsGridOptions, NavItemsGrid } from '@common/navkendo/navitemsgrid';
import { INavPageItemPropertyGroupOptions } from './navpageitempropertygroup';

/** Custom Control Generation Options */
export interface INavGenControlOptions {
    /** Explicit Detail Cols ... */
    CustomDetailCols?: IViewProperty[];
    /** Explicit MobileMode */
    Mobile: boolean;
    /** Controls get rendered in 'DesignMode' (override IgnoreMissing) */
    DesignMode?: boolean;
    /** NewLayout */
    NewLayout?: boolean;
}

/** Create Edit Control from Propertyvalue Object */
export function CreateEditControl(this: void, propValue: NavPropertyValue, context: INavEditContext,
    options: INavGenControlOptions): JQuery {
    var result: JQuery;
    var prop = propValue.Property;
    if (prop.HasLookup) {
        result = CreateLookup(propValue, context, options);
    }
    else {
        switch (prop.Type) {
            case "String":
                result = CreateInput(propValue, context, true, "text", options);
                break;

            case "Boolean":
                result = CreateCheckbox(propValue, context, options);
                break;

            case "DateTime":
                result = CreateDateTime(propValue, context);
                break;

            case "Int32":
                result = CreateNumeric(propValue, context, { format: "#", decimals: 0, step: 1 });
                break;

            case "Decimal":
                const config: kendo.ui.NumericTextBoxOptions = { step: 1 };
                switch (prop.DecimalNumericType) {
                    case NumericType.Currency:
                        config.format = "c2";
                        config.decimals = 16; // Nav precision is 5-16
                        break;
                    case NumericType.Percent:
                        config.format = "#.00 \\%";
                        break;
                    default:
                    // Noting to do
                }
                result = CreateNumeric(propValue, context, config);
                break;
            default:
                var arrProps = prop.ArrayProperties;
                if (arrProps) {
                    let arrElParent = $("<div></div>");
                    // Render Line Properties: Default as Grid, if explicitly desired as PropertyGroup
                    switch (arrProps.LineDisplayMode) {
                        case "ForceGroup":
                            // Create Property Groupd
                            let groupOptions: INavPageItemPropertyGroupOptions = {
                                Mobile: options.Mobile,
                                DesignMode: options.DesignMode,
                                item: propValue,
                                inColDirection: true,
                                view: {
                                    Title: "",
                                    ID: "",
                                    Properties: options.CustomDetailCols,
                                },
                                AttachedParent: context.GetAttachedParent() // problem : Lookups depend on ng Context on Creation ...
                            };
                            result = arrElParent.kendoNavPageItemPropertyGroup(groupOptions).data("kendoNavPageItemPropertyGroup").element;
                            break;

                        default:
                            // Create Grid Instance
                            let gridOptions: INavItemsGridOptions = {
                                Source: propValue,
                                customDetailCols: options.CustomDetailCols,
                                EditContext: context,
                                // 'actual base Mobile Option: TODO'
                                // Mobile: options.Mobile
                            };
                            const resultGrid = new NavItemsGrid(arrElParent, gridOptions);
                            resultGrid.bind(kconstants.NavDestroy, propValue.OnReadonlyChange(readonly => resultGrid.InfReadonly = readonly));
                            result = resultGrid.wrapper;
                            break;
                    }
                } else if (prop.EnumMappings) {
                    result = CreateEnumChoice(propValue, context);
                } else {
                    result = CreateInput(propValue, context, true, "text", options);
                }
                break;
        }
    }

    if (result) {
        // Enforce AutoSelect of Input on focus (exlcude 'k-formatted-value' for Numeric inputs which has 2 'representations')
        var inp = result.is("input") ? result : result.find("input:not(.k-formatted-value)").first();
        if (inp.length) {
            // window.setTimeout : necessary for Chrome, FF, Edge (but not IE)
            inp.on("focus.nav", () => {
                window.setTimeout(() => {
                    // only if still the active Element!
                    if (document.activeElement === inp[0]) {
                        inp.select();
                    }
                });
            });
            // Necessary in EDGE in some Situations (WTF);
            inp.on("blur.nav", () => inp.change());
            context.AddDispose(() => inp.off(".nav"));
        }

        result.addClass("navEditor").attr("navPropertyName", prop.Name);

        if ($.fn.kendoTooltip && prop.HintField && !(context.IsCellEditor == true)) {
            var tt = result.kendoTooltip({
                content: () => {
                    var currItem = propValue.CurrentItem;
                    if (!currItem)
                        return;
                    var strValue = currItem.GetDisplayValue(prop.HintField);
                    return $("<div>").text(strValue).html();
                },
            }).data("kendoTooltip");
            if (tt)
                context.AddDispose(tt.destroy.bind(tt));
        }
    }

    return result;
}

/** Create a Text Input Element to Edit/Display a a Page Property Value (String, CheckBox) */
function CreateInput(this: void, propValue: NavPropertyValue, context: INavEditContext, useDisplayValue: boolean, type: string, options: INavGenControlOptions): JQuery {
    var prop = propValue.Property;
    if (prop.LineCount > 1 && type != "checkbox")
        var result = $("<textarea style='resize: none; width: 100%' rows='" + prop.LineCount + "' cols='20' class='k-input k-textbox' ></textarea>");
    else
        var result = $("<input type='" + type + "' class='" + (type == "checkbox" ? "kendo-check-box" : "k-input k-textbox") + "' />");
    var counter = CreateCounter();
    if (prop.MaxLength > 0)
        result.attr("maxLength", prop.MaxLength);

    var getDisplayValue = () => propValue.GetValue(useDisplayValue);

    var setReadonly = (val: boolean) => {
        // disables selection, + scrolling [textarea]
        //result.toggleClass("k-state-disabled", val);
        if (val) {
            result.attr("disabled", "disabled");
        }
        else {
            result.removeAttr("disabled");
        }
    };

    var setInvalid = (val: boolean) => {
        result.toggleClass(kconstants.cssInvalid, !!val);
    };

    var applyValueToControl = () => {
        var newValue = getDisplayValue();
        if (type == "checkbox") {
            var cb = <HTMLInputElement>result[0];
            cb.checked = newValue == true;
        }
        else {
            result.val(newValue);
            if (newValue && newValue.length && newValue.length > 14)
                result.attr("title", newValue);
            else
                result.removeAttr("title");
        }
    };
    applyValueToControl();

    context.AddDispose(propValue.OnValueChange(() => counter.Wrap(applyValueToControl)));
    context.AddDispose(propValue.OnReadonlyChange(readonly => setReadonly(readonly)));
    context.AddDispose(propValue.OnInvalidChange(invalid => setInvalid(invalid)));
    context.AddDispose(() => result.off(".nav"));
    result.on("keydown.nav", e => {
        if (propValue.ReadOnly)
            return;

        switch (e.which) {
            case kendo.keys.ENTER:
                result.trigger("change");
                e.preventDefault();
                e.stopPropagation();
                break;
            default:
            // Nothing to do

        }
    });
    result.on("change.nav", (e: BaseJQueryEventObject) => {
        if (!counter.IsRunning()) {
            var toSet = type == "checkbox" ? result.is(":checked") : result.val();
            e.preventDefault();
            counter.WrapPromise(propValue.SetValue(toSet)).then(applyValueToControl, applyValueToControl);
        }
    });

    // special logic "CustProp" : If Hyperlink .-> Button to Open
    if (RenderAsLinkProps.indexOf(prop.Name) >= 0) {
        const button = $('<span style="cursor:pointer" title="Link Öffnen" class="k-select" role="button"  >' +
            `<span class="idms_Icon idms_LinkIcon idms_Icon_ExternalLink"></span>` +
            ' </span>');
        button.on("click", () => {
            window.open("" + propValue.GetValue(), "_blank");
        });
        var outer = $(`<div></div>`),
            input = result;
        button.css({ width: "18px", height: "22px", "float": "right", "padding-left": "8px", "padding-top": "4px" });
        outer.append(result, button);

        let refresh = (v) => {
            var show = fn.IsValidLink(v);
            button.toggle(show);
            input.css({ width: show ? "calc(100% - 26px)" : "100%" });
        };
        refresh(propValue.GetValue());
        context.AddDispose(propValue.OnValueChange(refresh));
        return outer;
    }

    return result;
}

function CreateCheckbox(this: void, propValue: NavPropertyValue, context: INavEditContext, options: INavGenControlOptions) {
    var checkbox = CreateInput(propValue, context, false, "checkbox", options);
    var checkboxContainer = $("<div class='idms_CheckBoxContainer'></div>");
    context.AddDispose(propValue.OnInvalidChange(invalid => { checkboxContainer.toggleClass(kconstants.cssInvalid, invalid); }));
    checkboxContainer.append(checkbox);
    return checkboxContainer;
}

function CreateNumeric(this: void, propValue: NavPropertyValue, context: INavEditContext, extConfig: kendo.ui.NumericTextBoxOptions): JQuery {
    var element = $("<input />");
    var outer = $("<div>");
    outer.append(element);
    var counter = CreateCounter();

    var applyValueToControl = () => dpx.value(propValue.GetValue());

    var onControlValChange = () => {
        if (!counter.IsRunning()) {
            counter.WrapPromise(propValue.SetValue(dpx.value())).then(applyValueToControl, applyValueToControl);
        }
    };
    var config = $.extend(<kendo.ui.NumericTextBoxOptions>{
        value: propValue.GetValue(),
        change: onControlValChange,
        spinners: false // Don't show up/down buttons
    }, extConfig);
    var dpx = element.kendoInfNumericTextBox(config).data("kendoInfNumericTextBox");

    // Hint for Currency --> Show Full Decimals
    if (typeof $.fn.kendoTooltip == "function" && propValue.Property.DecimalNumericType == NumericType.Currency) {
        var tTip = dpx.wrapper.kendoTooltip({
            content: (e) => {
                var strValue = propValue.CurrentItem && propValue.CurrentItem.GetDisplayValue(propValue.Property, {
                    FullDecimals: true
                });
                return $("<div></div>").text(strValue).html();
            },
        }).data("kendoTooltip");

        var origShow = tTip["_show"].bind(tTip);
        // Hack force Refresh on ToolTip Content on Value Change
        tTip.one("_ttShow", () => {
            var refreshTTip = fn.debounce(tTip.refresh.bind(tTip), 250);
            propValue.OnCurrentItemChanged(() => {
                refreshTTip();
                if (propValue.CurrentItem == null) {
                    tTip.destroy();
                }
            });
            propValue.OnValueChange(refreshTTip);
        });
        var _newShow = tTip["_show"] = (t) => {
            if (!propValue.GetValue())
                return;
            origShow(t);
            tTip.trigger("_ttShow"); // Allow show Event interception (see above)
        };
    }

    // TODO : Extract to class ????
    $([dpx.element[0], dpx["_text"][0]]).css({ "text-align": "right", "box-sizing": "border-box", "padding-right": "5px" });
    outer.children().css({ width: "100%" });
    // Disable default key increase/decrease action
    dpx.element.on("keydown.nav", function (e) {
        switch (e.which) {
            case kendo.keys.ENTER:
                if (!propValue.ReadOnly) {
                    dpx.element.trigger("change");
                    e.preventDefault();
                    e.stopPropagation();
                }
                break;
            default:
            // nothing to do
        }
        PreventKendoWidgetArrowAction(dpx.element, e);
    });

    context.AddDispose(propValue.OnValueChange(() => counter.Wrap(applyValueToControl)));
    context.AddDispose(propValue.OnReadonlyChange(readonly => { dpx.readonly(readonly); dpx.enable(!readonly); }));
    context.AddDispose(propValue.OnInvalidChange(invalid => { dpx.wrapper.toggleClass(kconstants.cssInvalid, !!invalid); }));
    context.AddDispose(() => {
        dpx.element.off(".nav");
        dpx.destroy();
    });
    return outer;
}

/** Create a Kendo DatePicker Control to Edit/Display a Property Value*/
function CreateDateTime(this: void, propValue: NavPropertyValue, context: INavEditContext): JQuery {
    var dtFormat = propValue.Property.DateFormat || 0,
        isTime = dtFormat == NavDtFormat.Time,
        kFormat = DateFormat[dtFormat],
        element = $("<input />"),
        counter = CreateCounter(),
        outer = $("<div>");
    outer.append(element);

    var applyValueToControl = () => dp.value(kendo.format(kFormat, propValue.GetValue()));

    var onControlValChange = () => {
        if (!counter.IsRunning()) {
            counter.WrapPromise(propValue.SetValue(dp.value())).then(applyValueToControl, applyValueToControl);
        }
    };

    //#region Custom "OnFocusOut" Value Validation with user feedback
    var lastErrValue: any;
    var validationWrapper = CreateCounter();

    // Manually handle change value on focusout to handle errors etc, (better way?)
    var handleCommitValue = (e: JQueryEventObject) => {
        if (validationWrapper.IsRunning()) {
            // Currently Resettings Value
            return;
        }
        var origVal = element.val();
        var value = ("" + origVal).trim();
        var isUnchanged = false;
        var custom = InfDatePicker.TranslateCustomInputValue(value);

        if (custom && custom !== fn.ToDateString(dp["_old"])) {
            dp.value(custom);
        } else {

            var strBefore = fn.ToDateString(dp.value()) || "";

            if (strBefore != value) {
                if (strBefore && strBefore === custom) {
                    // Reset display Value
                    dp.element.val(strBefore);
                } else {
                    dp.value(value);
                }
            }
            else
                isUnchanged = true;
        }
        var isValid = (value == "") || (dp.value() != null);

        if (!isValid) {
            //window.setTimeout(() => {
            validationWrapper.Wrap(() => {
                element.val(origVal).select().focus();
                if (lastErrValue != origVal) {
                    lastErrValue = origVal; // TODO : wOut : endless alert loop ...
                    // tslint:disable-next-line:ban
                    alert("'" + origVal + "' ist kein gültiges Datum");
                }
                //  });
            });
        } else if (!isUnchanged) {
            onControlValChange();
        }
    };

    element.on("focusout.nav", handleCommitValue);
    element.on("keydown.nav", e => {
        // ENTER gets handled this way in kendo
        if (e.keyCode == kendo.keys.ENTER) {
            handleCommitValue(e);
        }
    });
    //#endregion

    var dp = propValue.Property.DateFormat == NavDtFormat.Time ? element.kendoTimePicker({
        change: onControlValChange,
    }).data("kendoTimePicker") : element.kendoInfDatePicker({
        change: onControlValChange,
    }).data("kendoInfDatePicker");
    // set initial value
    applyValueToControl();
    context.AddDispose(propValue.OnValueChange(() => counter.Wrap(applyValueToControl)));
    context.AddDispose(propValue.OnReadonlyChange(readonly => { dp.readonly(readonly); dp.enable(!readonly); }));
    context.AddDispose(propValue.OnInvalidChange(invalid => { dp.wrapper.toggleClass(kconstants.cssInvalid, !!invalid); }));
    context.AddDispose(() => {
        dp.element.off(".nav");
        dp.destroy();
    });
    return outer;
}

/** Create a Kendo DropDonwList Control to Edit/Display a EnumMappings Base Property Value*/
function CreateEnumChoice(this: void, propValue: NavPropertyValue, context: INavEditContext): JQuery {
    var element = $("<input />");
    var outer = $("<div>");
    outer.append(element);
    var counter = CreateCounter();
    var isOpened = false;

    // Setup Dynamic Enum Values Availability Functionality
    const createCurrDs = (invalidValues?: number[]) => {
        const currValue = propValue.GetValue();
        invalidValues = invalidValues || [];
        const validValues = propValue.Property.EnumMappings.filter(item =>
            (!item.Invalid && !(invalidValues.indexOf(item.Value) >= 0)) || (item.Value == currValue));
        return new kendo.data.DataSource({ data: validValues });
    };
    let currDs = createCurrDs();
    let reset = fn.noop;
    const setCurrItem = () => {
        reset();
        if (propValue && propValue.CurrentItem) {
            reset = propValue.CurrentItem.RegisterEnumStateChanged(propValue.Property, (e: { Invalid: number[] }) => {
                currDs = createCurrDs(e.Invalid);
                dp?.setDataSource(currDs);
            });
        }
    };
    propValue.OnCurrentItemChanged(setCurrItem);
    setCurrItem();

    var applyValueToControl = () => propValue && dp.value(propValue.GetValue());
    var dp = <kendo.ui.DropDownList>element.kendoDropDownList(<kendo.ui.DropDownListOptions>{
        dataSource: currDs,
        dataTextField: "Title",
        dataValueField: "Value",
        valuePrimitive: true,
        value: propValue.GetValue(),
        change: () => {
            if (!counter.IsRunning()) {
                counter.WrapPromise(propValue.SetValue(dp.value())).then(applyValueToControl, applyValueToControl);
            }
        },
        open: () => { isOpened = true; },
        close: () => { isOpened = false; }
    }).data("kendoDropDownList");

    // Disabled state
    var setReadonly = (val: boolean) => {
        dp.readonly(val);
        dp.wrapper.toggleClass("k-state-disabled", !!val);
    };

    // Disable default arrow key item change action
    dp.wrapper.on("keydown.nav", function (e) {
        switch (e.which) {
            case kendo.keys.ENTER:
                if (!isOpened) {
                    // Open DropDown with enter, but prevent choice action
                    e.stopImmediatePropagation();
                    dp.open();
                }
                break;

            case kendo.keys.SPACEBAR:
                // Open DropDown with space
                dp.open();
                break;

            default:
                if (!isOpened) {
                    PreventKendoWidgetArrowAction(dp.element, e);
                } else {
                    // Prevent arrow key action, but allow navigation
                    e.stopPropagation();
                }
                break;
        }
    });

    context.AddDispose(() => {
        dp.wrapper.off(".nav");
        dp.destroy();
        propValue = null;
    });
    context.AddDispose(propValue.OnValueChange(() => counter.Wrap(applyValueToControl)));
    context.AddDispose(propValue.OnReadonlyChange(readonly => setReadonly(readonly)));
    context.AddDispose(propValue.OnInvalidChange(invalid => { dp.wrapper.toggleClass(kconstants.cssInvalid, !!invalid); }));
    return outer;
}

/** Create Display for Lookup Attached Data */
function CreateLookup(this: void, propValue: NavPropertyValue, context: INavEditContext, options: INavGenControlOptions): JQuery {
    var parent = context.GetAttachedParent(),
        prop = propValue.Property,
        injector = <ng.auto.IInjectorService>angular.element(parent).injector(),
        settings = (injector && injector.get<INavCustomization>("NavCustomization")) || {},
        lookupSettings = (settings && settings.LookupSettings) || {};

    var lookupCtrl = new NavLookupControl("<span class='k-picker-wrap'></span>", {
        MaxRead: prop.MaxRead || lookupSettings.MaxRead,
        MinSearch: prop.MinSearch === 0 || prop.MinSearch > 0 ? prop.MinSearch : lookupSettings.MinSearch,
        NavProperty: propValue,
        AttachedParent: context.GetAttachedParent()
    });

    if (options.NewLayout && !options.Mobile) {
        // TODO msqqq
        lookupCtrl.autoComplete.element.height("24px");
    }
    context.AddDispose(() => lookupCtrl.destroy());
    return lookupCtrl.element;
}

/** Prevent kendo widget arrow key actions to allow arrow navigation */
function PreventKendoWidgetArrowAction(this: void, elem: JQuery, e: JQueryEventObject) {
    var arrowKeys = [kendo.keys.LEFT, kendo.keys.RIGHT, kendo.keys.UP, kendo.keys.DOWN];

    if (arrowKeys.indexOf(e.which) >= 0) {
        e.stopImmediatePropagation();

        // Trigger grid navigation
        var currCell = elem.closest(".k-edit-cell");

        // Emulate keydown on parent container to prevent widget action and keep event bubble
        var pEvent = jQuery.Event("keydown");
        pEvent.which = e.which;
        currCell.trigger(pEvent);
    }
}
